import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Palette, Users, Settings, ShieldAlert, Plus, Trash2, Upload, Mail, Building2, Armchair } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LoadingSpinner, AppLayout } from '../components/Layout';
import ProgramsPanel from '../components/org/ProgramsPanel';
import RosterPanel from '../components/org/RosterPanel';
import ProgramDashboard from '../components/org/ProgramDashboard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const err = (e, fallback) => e?.response?.data?.detail?.message || e?.response?.data?.detail || fallback;

/** The organization's own console: their instance only, theirs to change. */
export default function OrgConsolePage() {
  const { getAuthHeader } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [brand, setBrand] = useState({});
  const [settings, setSettings] = useState({});
  const [seatForm, setSeatForm] = useState({ seat_count: '', credits_per_seat: '' });
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invite, setInvite] = useState({ email: '', full_name: '', role: 'org_admin' });
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      let orgId = params.get('org');
      if (!orgId) {
        const list = await axios.get(`${API}/orgs`, getAuthHeader());
        orgId = list.data.organizations?.[0]?.id;
        if (!orgId) return setError('You are not attached to an organization yet.');
      }
      const r = await axios.get(`${API}/orgs/${orgId}`, getAuthHeader());
      setData(r.data);
      const o = r.data.organization;
      setBrand(o.branding || {});
      setSettings({ name: o.name || '', location: o.location || '', website: o.website || '',
        description: o.description || '', status: o.status || 'setup',
        primary_contact_name: o.primary_contact_name || '',
        primary_contact_email: o.primary_contact_email || '' });
      setSeatForm({ seat_count: String(r.data.seats?.total ?? 0),
        credits_per_seat: r.data.wallet?.credits_per_seat != null
          ? String(r.data.wallet.credits_per_seat) : '' });
      const s = await axios.get(`${API}/orgs/${orgId}/staff`, getAuthHeader()).catch(() => null);
      if (s) { setStaff(s.data.staff); setRoles(s.data.roles); }
      const a = await axios.get(`${API}/orgs/${orgId}/audit`, getAuthHeader()).catch(() => null);
      if (a) setAudit(a.data.entries.slice(0, 8));
    } catch (e) {
      setError(err(e, 'Could not open that organization'));
    }
  }, [getAuthHeader, params]);
  useEffect(() => { load(); }, [load]);

  const can = (p) => (data?.my_permissions || []).includes(p);
  const org = data?.organization;
  const seats = data?.seats || { total: 0, used: 0, label: '' };
  const seatPct = seats.total ? Math.min(100, Math.round((seats.used / seats.total) * 100)) : 0;

  const saveBrand = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/orgs/${org.id}/branding`, brand, getAuthHeader());
      toast.success('Branding updated, your portal reflects it immediately');
      load();
    } catch (e) { toast.error(err(e, 'Could not save')); }
    finally { setSaving(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/orgs/${org.id}`, settings, getAuthHeader());
      toast.success('Saved');
      load();
    } catch (e) { toast.error(err(e, 'Could not save')); }
    finally { setSaving(false); }
  };

  const saveSeats = async () => {
    try {
      const payload = { seat_count: Number(seatForm.seat_count || 0) };
      if (data.wallet && seatForm.credits_per_seat !== '') {
        payload.credits_per_seat = Number(seatForm.credits_per_seat);
      }
      const r = await axios.put(`${API}/orgs/${org.id}/seats`, payload, getAuthHeader());
      toast.success(r.data.seats.label);
      load();
    } catch (e) { toast.error(err(e, 'Could not update their places')); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API}/orgs/${org.id}/logo`, form, {
        ...getAuthHeader(), headers: { ...getAuthHeader().headers } });
      setBrand(b => ({ ...b, logo_url: r.data.logo_url }));
      toast.success('Logo uploaded');
      load();
    } catch (e) { toast.error(err(e, 'Could not upload that image')); }
  };

  const addStaff = async () => {
    try {
      await axios.post(`${API}/orgs/${org.id}/invite-admin`, invite, getAuthHeader());
      toast.success(`Invitation sent to ${invite.email}`);
      setInvite({ email: '', full_name: '', role: 'org_admin' });
      load();
    } catch (e) { toast.error(err(e, 'Could not add them')); }
  };

  const resend = async (id) => {
    try {
      const r = await axios.post(`${API}/orgs/${org.id}/staff/${id}/resend-invite`, {}, getAuthHeader());
      navigator.clipboard?.writeText(r.data.invite_link);
      toast.success('Invitation re-sent, link copied');
    } catch (e) { toast.error(err(e, 'Could not resend')); }
  };

  const removeStaff = async (id) => {
    try {
      await axios.delete(`${API}/orgs/${org.id}/staff/${id}`, getAuthHeader());
      toast.success('Removed'); load();
    } catch { toast.error('Could not remove them'); }
  };

  if (error) return <AppLayout><div className="p-8 text-sm text-rose-300" data-testid="org-console-error">{error}</div></AppLayout>;
  if (!org) return <AppLayout><div className="p-16 flex justify-center"><LoadingSpinner /></div></AppLayout>;

  const field = (key, label, long = false) => (
    <div className="space-y-2" key={key}>
      <Label className="text-slate-300 text-sm">{label}</Label>
      {long
        ? <Textarea value={brand[key] || ''} onChange={e => setBrand({ ...brand, [key]: e.target.value })}
            className="input-dark min-h-[90px]" data-testid={`brand-${key}`} />
        : <Input value={brand[key] || ''} onChange={e => setBrand({ ...brand, [key]: e.target.value })}
            className="input-dark" data-testid={`brand-${key}`} />}
    </div>
  );

  return (
    <AppLayout>
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="org-console-page">
      {data.support_mode && (
        <div className="mb-5 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-100"
          data-testid="support-mode-banner">
          FuturePath support access, you're inside {org.name}'s instance. Everything you do here is recorded.
        </div>
      )}

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl border border-white/15 flex items-center justify-center shrink-0 overflow-hidden"
          style={{ background: `${brand.primary_color || '#22d3ee'}18` }}>
          {brand.logo_url
            ? <img src={brand.logo_url} alt="" className="w-full h-full object-contain p-1" data-testid="org-logo" />
            : <Building2 className="w-6 h-6 text-slate-300" />}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">
            {(org.org_type || '').replace(/_/g, ' ')} · {data.my_role?.replace(/_/g, ' ')}
          </p>
          <h1 className="font-outfit text-3xl md:text-4xl font-semibold text-white mt-1.5">{org.name}</h1>
          <p className="text-sm text-slate-300 mt-2 font-mono" data-testid="org-portal-url">{data.portal_url}</p>
        </div>
      </motion.div>

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4" data-testid="seat-summary">
          <p className="text-xs text-slate-300 inline-flex items-center gap-1.5">
            <Armchair className="w-3.5 h-3.5" /> Places
          </p>
          <p className="text-2xl text-white font-semibold mt-1 fp-num">
            {seats.used}{seats.total ? ` / ${seats.total}` : ''}
          </p>
          {!!seats.total && (
            <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full"
                style={{ width: `${seatPct}%`,
                  background: seatPct >= 100 ? '#f59e0b' : (brand.primary_color || '#22d3ee') }} />
            </div>
          )}
          <p className="text-xs text-slate-300 mt-2">{seats.label}</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
          <p className="text-xs text-slate-300">Programs · team</p>
          <p className="text-2xl text-white font-semibold mt-1 fp-num">
            {data.counts.programs} · {data.counts.staff}
          </p>
          <p className="text-xs text-slate-300 mt-2">Join code: {(org.invite_codes || []).join(', ') || '-'}</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
          <p className="text-xs text-slate-300">Status</p>
          <p className="text-2xl text-white font-semibold mt-1 capitalize">{org.status}</p>
          {data.wallet && (
            <p className="text-xs text-slate-300 mt-2" data-testid="internal-allowance">
              Internal AI allowance: {data.wallet.remaining.toLocaleString()} of{' '}
              {data.wallet.granted.toLocaleString()} left, FuturePath staff only
            </p>
          )}
        </div>
      </div>

      {can('manage_org') && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6" data-testid="seats-panel">
          <div className="flex items-center gap-2">
            <Armchair className="w-4 h-4 text-emerald-300" />
            <h2 className="font-outfit text-xl font-semibold text-white">Their places</h2>
          </div>
          <p className="text-sm text-slate-300 mt-1.5">
            How many young people this organization can enrol. Change it any time, enrolment stops
            politely at the limit rather than failing silently.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-4 items-end">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Places</Label>
              <Input type="number" value={seatForm.seat_count}
                onChange={e => setSeatForm({ ...seatForm, seat_count: e.target.value })}
                className="input-dark" data-testid="seat-count-input" />
            </div>
            {data.wallet && (
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">AI allowance per place (internal)</Label>
                <Input type="number" value={seatForm.credits_per_seat}
                  onChange={e => setSeatForm({ ...seatForm, credits_per_seat: e.target.value })}
                  className="input-dark" data-testid="credits-per-seat-input" />
              </div>
            )}
            <Button onClick={saveSeats} data-testid="save-seats"
              className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
              Save places
            </Button>
          </div>
        </div>
      )}

      {can('view_participants') && (
        <div className="mt-6">
          <ProgramDashboard orgId={org.id} accent={brand.primary_color || '#22d3ee'}
            canManage={can('invite_participants')} />
        </div>
      )}

      {can('view_participants') && (
        <ProgramsPanel orgId={org.id} slug={org.slug} accent={brand.primary_color || '#22d3ee'}
          canManage={can('manage_programs')} onChanged={load} />
      )}

      {can('view_participants') && (
        <RosterPanel orgId={org.id} accent={brand.secondary_color || '#a78bfa'}
          canManage={can('invite_participants')} onChanged={load} />
      )}

      {can('manage_branding') && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6" data-testid="branding-panel">
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-cyan-300" />
            <h2 className="font-outfit text-xl font-semibold text-white">Your branding</h2>
          </div>
          <p className="text-sm text-slate-300 mt-1.5">
            This is what your families and participants see when they arrive.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-5">
            <div className="w-16 h-16 rounded-xl border border-white/15 flex items-center justify-center overflow-hidden"
              style={{ background: `${brand.primary_color || '#22d3ee'}18` }}>
              {brand.logo_url
                ? <img src={brand.logo_url} alt="" className="w-full h-full object-contain p-1" />
                : <Building2 className="w-6 h-6 text-slate-300" />}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={e => uploadLogo(e.target.files?.[0])} data-testid="logo-file" />
            <Button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-logo"
              className="rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload your logo
            </Button>
            <span className="text-xs text-slate-300">PNG, JPG or SVG · under 600KB</span>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            {field('display_name', 'Display name')}
            {field('portal_title', 'Portal title')}
            {['primary_color', 'secondary_color', 'accent_color'].map(k => (
              <div className="space-y-2" key={k}>
                <Label className="text-slate-300 text-sm">{k.replace('_', ' ')}</Label>
                <div className="flex gap-2 items-center">
                  <Input value={brand[k] || ''} onChange={e => setBrand({ ...brand, [k]: e.target.value })}
                    className="input-dark" data-testid={`brand-${k}`} />
                  <input type="color" value={brand[k] || '#22d3ee'}
                    onChange={e => setBrand({ ...brand, [k]: e.target.value })}
                    className="w-9 h-9 rounded-lg bg-transparent border border-white/20 shrink-0 cursor-pointer" />
                </div>
              </div>
            ))}
            {field('career_center', 'Career centre / support line')}
            <div className="sm:col-span-2">{field('welcome_message', 'Welcome message', true)}</div>
            {field('powered_by_text', 'Footer text')}
          </div>
          <Button onClick={saveBrand} disabled={saving} data-testid="save-branding"
            className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold mt-5">
            Save branding
          </Button>
        </div>
      )}

      {can('manage_org') && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6" data-testid="settings-panel">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-sky-300" />
            <h2 className="font-outfit text-xl font-semibold text-white">Organization details</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {[['name', 'Name'], ['location', 'Location'], ['website', 'Website'],
              ['primary_contact_name', 'Primary contact'], ['primary_contact_email', 'Contact email']]
              .map(([k, label]) => (
                <div className="space-y-2" key={k}>
                  <Label className="text-slate-300 text-sm">{label}</Label>
                  <Input value={settings[k] || ''} onChange={e => setSettings({ ...settings, [k]: e.target.value })}
                    className="input-dark" data-testid={`setting-${k}`} />
                </div>
              ))}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Status</Label>
              <Select value={settings.status} onValueChange={v => setSettings({ ...settings, status: v })}>
                <SelectTrigger className="input-dark" data-testid="setting-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['setup', 'active', 'paused', 'archived'].map(s =>
                    <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-slate-300 text-sm">What they do (portal description)</Label>
              <Textarea value={settings.description || ''}
                onChange={e => setSettings({ ...settings, description: e.target.value })}
                className="input-dark min-h-[80px]" data-testid="setting-description" />
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving} data-testid="save-settings"
            className="rounded-full bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold mt-5">
            Save details
          </Button>
        </div>
      )}

      {can('manage_staff') && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6" data-testid="staff-panel">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-300" />
            <h2 className="font-outfit text-xl font-semibold text-white">Your team</h2>
          </div>
          <div className="mt-4 space-y-2">
            {staff.filter(s => s.status !== 'removed').map(s => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-800/40 px-4 py-2.5"
                data-testid={`staff-${s.id}`}>
                <span className="text-sm text-white">{s.full_name}</span>
                <span className="text-xs text-slate-300">{s.email}</span>
                <span className="text-[10px] uppercase tracking-wider text-violet-300/90">
                  {s.role.replace(/_/g, ' ')}
                </span>
                {s.status === 'invited' && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-300/90">
                    invited, hasn't set a password
                  </span>
                )}
                <div className="ml-auto flex items-center gap-3">
                  {s.status === 'invited' && (
                    <button onClick={() => resend(s.id)} data-testid={`resend-${s.id}`}
                      className="text-xs text-slate-300 hover:text-cyan-300 inline-flex items-center gap-1">
                      <Mail className="w-3 h-3" /> resend
                    </button>
                  )}
                  <button onClick={() => removeStaff(s.id)} data-testid={`remove-staff-${s.id}`}
                    className="text-slate-300 hover:text-rose-300 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-5 gap-3 mt-5">
            <Input placeholder="Their name" value={invite.full_name}
              onChange={e => setInvite({ ...invite, full_name: e.target.value })}
              className="input-dark" data-testid="invite-name" />
            <Input placeholder="Their email" value={invite.email}
              onChange={e => setInvite({ ...invite, email: e.target.value })}
              className="input-dark sm:col-span-2" data-testid="invite-email" />
            <Select value={invite.role} onValueChange={v => setInvite({ ...invite, role: v })}>
              <SelectTrigger className="input-dark" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {roles.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={addStaff} disabled={!invite.email} data-testid="invite-submit"
              className="rounded-full bg-violet-500 hover:bg-violet-400 text-white font-semibold">
              <Plus className="w-4 h-4 mr-1" /> Invite
            </Button>
          </div>
          <p className="text-xs text-slate-300 mt-3">
            They get an email to create their own password. Roles decide what they can reach,             facilitators and counsellors see participation, not branding or places.
          </p>
        </div>
      )}

      {audit.length > 0 && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-5" data-testid="org-audit">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-300" />
            <h2 className="font-outfit text-lg font-semibold text-white">Recent activity</h2>
          </div>
          <ul className="mt-3 space-y-1.5">
            {audit.map((e, i) => (
              <li key={i} className="text-xs text-slate-300">
                {String(e.created_at).slice(0, 16).replace('T', ' ')} · {e.action}
                {e.resource ? ` · ${e.resource}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!can('manage_branding') && !can('manage_staff') && (
        <div className="mt-8 rounded-2xl border border-white/10 bg-slate-900/60 p-6" data-testid="limited-role-note">
          <Settings className="w-4 h-4 text-slate-300" />
          <p className="text-sm text-slate-300 mt-2">
            Your role gives you participation and reporting access. Ask an owner or admin at{' '}
            {org.name} if you need to change branding or the team.
          </p>
        </div>
      )}
    </div>
    </AppLayout>
  );
}
